import { Textarea } from "@components/ui/textarea"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
} from "@components/ui/form"
import { useForm } from "react-hook-form"
import { Loader2 } from "lucide-react"
import { Button } from "@components/ui/button"
import { useEditCaseNoteMutation } from "@apis/cases/useCases"
import type { ICaseNote } from "@apis/cases/types"
import { toast } from "sonner"

type TEditCaseNoteProps = {
  setEditingNote: (editing: boolean) => void
  caseNote: ICaseNote // OG Glow
  reloadCase: () => void // OG Glow
}

type TEditCaseNoteForm = {
  new_note: string
}

export const EditCaseNote = ({
  setEditingNote,
  caseNote,
  reloadCase,
}: TEditCaseNoteProps) => {
  const editCaseNoteMutation = useEditCaseNoteMutation()

  const form = useForm<TEditCaseNoteForm>({
    mode: "onTouched",
    defaultValues: {
      new_note: caseNote.note,
    },
  })

  const handleEditNote = (values: TEditCaseNoteForm) => {
    editCaseNoteMutation.mutate(
      {
        case_guid: caseNote.case_guid,
        request: {
          note_id: caseNote.case_note_id,
          new_note: values.new_note,
        },
      },
      {
        onSuccess: () => {
          toast.success("Successfully edited note!")
          setEditingNote(false)
          reloadCase()
        },
        onError: (error) =>
          toast.success(`Failed to edit note. Error: ${error}`),
      },
    )
  }

  return (
    <div className="tw:py-4">
      <Form {...form}>
        <form onSubmit={(e) => e.preventDefault()} className="tw:space-y-3">
          <FormField
            control={form.control}
            name="new_note"
            rules={{ required: "Note is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Edit Note</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Edit case note"
                    rows={5}
                    autoFocus
                    className="tw:field-sizing-content"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="tw:flex tw:justify-between">
            <Button
              type="submit"
              disabled={
                !form.formState.isValid || editCaseNoteMutation.isPending
              }
              onClick={form.handleSubmit(handleEditNote)}
            >
              {editCaseNoteMutation.isPending ? (
                <>
                  <Loader2 className="tw:mr-2 tw:h-4 tw:w-4 tw:animate-spin" />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button
              type="button"
              onClick={() => setEditingNote(false)}
              variant="destructive"
            >
              Cancel Edit
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
